"use client";

import { cn } from "@/lib/utils/general-utils";
import * as React from "react";

// Propagates the "compact" density setting from <Table> to child cells.
const TableCompactContext = React.createContext(false);

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  compact?: boolean;
  /** When true, wrapper only scrolls horizontally (for use inside vertical scroll containers). */
  horizontalScrollOnly?: boolean;
  /**
   * Skip overflow on the wrapper when a parent already scrolls.
   * Nested overflow-auto wrappers push the horizontal scrollbar below the fold.
   */
  disableWrapperOverflow?: boolean;
  /** Extra classes for the overflow wrapper around <table>. */
  wrapperClassName?: string;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    {
      className,
      compact = false,
      horizontalScrollOnly = false,
      disableWrapperOverflow = false,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    return (
      <TableCompactContext.Provider value={compact}>
        <div
          className={cn(
            "relative w-full min-w-0 scrollbar-thin",
            disableWrapperOverflow
              ? "overflow-visible"
              : horizontalScrollOnly
                ? "overflow-x-auto overflow-y-visible"
                : "overflow-auto",
            wrapperClassName,
          )}
        >
          <table
            ref={ref}
            className={cn("w-full caption-bottom text-sm", className)}
            {...props}
          />
        </div>
      </TableCompactContext.Provider>
    );
  },
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn("[&_tr]:border-b [&_tr]:border-gray-200", className)}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn("border-t border-gray-200 font-medium", className)}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b border-gray-200 transition-colors hover:bg-gray-50/50 data-[state=selected]:bg-blue-50/50",
      className,
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => {
  const compact = React.useContext(TableCompactContext);
  return (
    <th
      ref={ref}
      className={cn(
        compact ? "h-8 px-3" : "h-10 px-4",
        "first:pl-0 last:pr-0 text-left align-middle text-xs font-medium text-text-tertiary [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  );
});
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => {
  const compact = React.useContext(TableCompactContext);
  return (
    <td
      ref={ref}
      className={cn(
        compact ? "px-3 py-1.5" : "px-4 py-2.5",
        "first:pl-0 last:pr-0 align-middle text-sm text-gray-700 [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  );
});
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-text-muted", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
